// Docs · Workflows, Recipes, Environment variables, Errors and fixes, Rules, Glossary.
import { C, Code, DocLink, Endpoint, Flow, H2, H3, Lead, List, Note, P, Params, Table } from '../parts'

export function Workflows() {
  return (
    <>
      <Lead>Your endpoints are the steps; a workflow puts them in order and turns the last step into the answer the web app gets.</Lead>
      <Flow items={[['Start', 'the applicant'], ['Dedupe check', 'ok / duplicate / error'], ['Create lead', 'ok / rejected / error'], ['Get offer', 'offer / pending / rejected'], ['Answer', 'what the app gets']]} />
      <H2>The nodes</H2>
      <Table head={['Node', 'Does']} rows={[
        ['Start', 'the standard applicant'],
        ['API', 'runs one of your endpoints; one line out per outcome. Settings: wait per call (seconds)'],
        ['Decision', 'a condition without code → yes / no'],
        ['Wait', 'a pause, counted against the workflow’s total wait'],
        ['Answer', 'the end: one of offer, approved, redirect, pending, rejected, duplicate, error, with an optional message'],
      ]} />
      <H2>How data moves between steps</H2>
      <List>
        <li>Each API step gets the same applicant and <C>ctx.prev</C> = what the step before returned.</li>
        <li><C>lender_ref</C> is carried: a step that does not return it still passes on the earlier one.</li>
        <li>An outcome that is not one of the endpoint’s lines, a failed system check or a thrown error → the step goes out on <C>error</C>.</li>
      </List>
      <H2>Conditions (Decision nodes and lines)</H2>
      <P>Field · operator · value, no code. Fields: <C>applicant.incomeMonth</C>, <C>prev.reply_code</C>, <C>prev.offers.length</C>, <C>steps.Dedupe check</C> (that step’s outcome).</P>
      <Table head={['Operator', 'True when']} rows={[['eq · neq', 'equal · not equal'], ['gt · gte · lt · lte', 'greater / less (numbers)'], ['contains', 'text or list contains the value'], ['exists · missing', 'the field is there / not there']]} />
      <H2>The answer the web app gets</H2>
      <Params rows={[
        ['status', 'answer kind', true, 'the Answer node’s kind'],
        ['lender_ref', 'string | null', true, 'the last known lender_ref'],
        ['offers', '{ amount, rate_pa, tenure_months, emi? }[]', true, 'from the last step’s offers (empty otherwise)'],
        ['links.continue', 'string | null', true, 'the last step’s link (null for rejected / error)'],
        ['message', 'string', true, 'the Answer node’s message, else the step’s message'],
        ['lender_reply', '{ masked: true, log_id }', true, 'where the lender’s exact reply is kept (30 days, admins)'],
      ]} />
      <H2>Service endpoints and calls</H2>
      <P>3.5 gives each product an endpoint id (e.g. <C>echo.personal</C>) with one workflow version behind it, UAT only or UAT + production. The web app calls:</P>
      <Endpoint method="POST" path="/v1/applications" note="x-app-key; { endpoint_id, applicant } → the answer" />
      <P>Developers and Postman can call one endpoint of the live batch directly:</P>
      <Endpoint method="POST" path="/lenders/{slug}/{route}" note="x-hub-key; { input, prev? } → the run result" />
      <H2>Keys, scopes and limits</H2>
      <P>Every call needs a key of a <b>consumer</b> (Settings → Key management): the web app, the website, the developers (Postman) or one an admin added. The consumer’s scopes say what its keys may call; its limits say how often. Only <C>GET /health</C> needs no key.</P>
      <Table head={['Answer', 'Means', 'Do']} code={false} rows={[
        ['401 bad_key', 'no key, a wrong one, or it was revoked, has ended, or its consumer is off', 'ask an admin for a key (shown once when made)'],
        ['403 not_allowed', 'the key works but its consumer may not call this (e.g. a UAT-only key on production)', 'an admin gives the consumer that scope'],
        ['429 rate_limited', 'more calls than the limit (per minute, with a burst)', 'wait Retry-After seconds; RateLimit-Remaining says how many are left'],
        ['429 quota_used', 'the daily quota is used (India day)', 'it opens again at midnight India time, or an admin raises it'],
        ['503 no_key', 'no consumer has a working key for this API on this Hub', 'an admin makes one'],
      ]} />
      <Note><b>Run all paths</b> (3.4) runs every way through the workflow with your test events; it must be green before a workflow can go live. A workflow step’s “retries” setting is shown on the step but not repeated yet: handle a lender’s temporary error in the code if it matters.</Note>
    </>
  )
}

export const LOGIN = `// src/services/lender.js: the lender's address and login in one place, used by every endpoint.
const tokens = {}   // one token per environment; kept while the container runs

function url(ctx, path) {
  return ctx.env.LENDER_BASE_URL + path
}

async function token(ctx) {
  if (ctx.mode === 'sample') return 'test-token'            // a test event gives every call the same saved reply: no login
  const kept = tokens[ctx.environment]
  if (kept && kept.until > Date.now() + 60000) return kept.value   // still valid for at least a minute
  const reply = await ctx.lender({
    method: 'POST',
    url: url(ctx, '/oauth/token'),
    headers: { 'Content-Type': 'application/json' },
    body: { client_id: ctx.secret('LENDER_CLIENT_ID'), client_secret: ctx.secret('LENDER_CLIENT_SECRET') },
    timeoutMs: 8000,
  })
  if (reply.status !== 200 || !reply.body || !reply.body.access_token) throw new Error('login refused: HTTP ' + reply.status)
  tokens[ctx.environment] = { value: reply.body.access_token, until: Date.now() + (reply.body.expires_in || 300) * 1000 }
  return reply.body.access_token
}

async function headers(ctx) {
  return { Authorization: 'Bearer ' + (await token(ctx)), 'Content-Type': 'application/json' }
}

module.exports = { url, token, headers }`

export const LEAD = `const { defineApi } = require('@firstfund/lender-sdk')
const lender = require('../../services/lender')

const EMPLOYMENT = { salaried: 'SALARIED', self_employed: 'SELF_EMPLOYED', student: 'OTHERS', other: 'OTHERS' }

module.exports = defineApi({
  title: 'Create lead',
  outcomes: ['ok', 'duplicate', 'rejected', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({
      method: 'POST',
      url: lender.url(ctx, '/v1/leads'),
      headers: await lender.headers(ctx),
      body: {
        partner_ref: ctx.ref,
        name: applicant.name, mobile: applicant.mobile, pan: applicant.pan, dob: applicant.dob,
        pincode: applicant.pincode, monthly_income: applicant.incomeMonth,
        employment_type: EMPLOYMENT[applicant.employment], consent_id: applicant.consentId,
      },
      timeoutMs: 10000,
    })
    const b = reply.body || {}
    if (reply.status === 201) return { outcome: 'ok', reply_code: 'LEAD_CREATED', lender_ref: String(b.lead_id) }
    if (reply.status === 409) return { outcome: 'duplicate', reply_code: b.code || 'DUPLICATE' }
    if (reply.status === 422) return { outcome: 'rejected', reply_code: b.code || 'NOT_ELIGIBLE', message: 'This lender cannot offer you a loan right now' }
    return { outcome: 'error', reply_code: reply.status ? b.code || 'HTTP_' + reply.status : 'NO_REPLY' }
  },
})`

export const OFFER = `const { defineApi } = require('@firstfund/lender-sdk')
const lender = require('../../services/lender')

module.exports = defineApi({
  title: 'Get offer',
  outcomes: ['offer', 'pending', 'rejected', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({
      method: 'GET',
      url: lender.url(ctx, '/v1/leads/' + encodeURIComponent(ctx.prev.lender_ref) + '/offers'),
      headers: await lender.headers(ctx),
      timeoutMs: 15000,
    })
    const b = reply.body || {}
    if (reply.status === 200 && b.status === 'APPROVED' && Array.isArray(b.offers) && b.offers.length) {
      return {
        outcome: 'offer',
        reply_code: 'APPROVED',
        offers: b.offers.map((o) => ({ amount: Number(o.loan_amount), tenureMonths: Number(o.tenure), ratePa: Number(o.roi), emi: Number(o.emi) || undefined })),
      }
    }
    if (reply.status === 200 && b.status === 'IN_PROGRESS') return { outcome: 'pending', reply_code: 'IN_PROGRESS', retryAfterMs: 30000 }
    if (reply.status === 200 && b.status === 'REJECTED') return { outcome: 'rejected', reply_code: b.reason_code || 'REJECTED' }
    return { outcome: 'error', reply_code: reply.status ? 'HTTP_' + reply.status : 'NO_REPLY' }
  },
})`

const REDIRECT = `if (reply.status === 200 && typeof b.journey_url === 'string' && b.journey_url.startsWith('https://')) {
  return { outcome: 'redirect', reply_code: 'CONTINUE_AT_LENDER', link: b.journey_url, lender_ref: b.lead_id }
}`

const FORM = `const reply = await ctx.lender({
  method: 'POST',
  url: ctx.env.LENDER_BASE_URL + '/api/check',
  headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Api-Key': ctx.secret('LENDER_API_KEY') },
  body: new URLSearchParams({ mobile: applicant.mobile, pan: applicant.pan }).toString(),   // a string is sent as it is
  timeoutMs: 8000,
})`

const SIGN = `const crypto = require('node:crypto')

// a lender that wants every body signed with HMAC-SHA256
function signed(ctx, body) {
  const text = JSON.stringify(body)
  const signature = crypto.createHmac('sha256', ctx.secret('LENDER_SIGNING_KEY')).update(text).digest('hex')
  return { body: text, headers: { 'Content-Type': 'application/json', 'X-Signature': signature } }
}

const s = signed(ctx, { mobile: applicant.mobile })
const reply = await ctx.lender({ method: 'POST', url: ctx.env.LENDER_BASE_URL + '/v1/check', headers: s.headers, body: s.body, timeoutMs: 8000 })`

export const CODES = `// the lender's codes → our outcomes, in one table that is easy to extend
const CODES = {
  E_1001: ['rejected', 'Age outside the lender’s range'],
  E_1002: ['rejected', 'Income too low for this lender'],
  E_2001: ['duplicate', 'You already applied with this lender'],
}

const known = CODES[b.error_code]
if (known) return { outcome: known[0], reply_code: b.error_code, message: known[1] }
return { outcome: 'error', reply_code: b.error_code || 'HTTP_' + reply.status }   // a new code → Captured codes + an Alert`

export function Recipes() {
  return (
    <>
      <Lead>Ready pieces to copy into your batch. Change the lender’s paths, field names and codes; keep the shape.</Lead>
      <H2 id="login-token">Login token</H2>
      <P>Many lenders give a token that lasts some minutes. Get it once, keep it until it ends. Put this in <C>src/services/lender.js</C> and use <C>lender.headers(ctx)</C> in every endpoint. Set <C>LENDER_CLIENT_ID</C> and <C>LENDER_CLIENT_SECRET</C> in tab 5.</P>
      <Code title="src/services/lender.js">{LOGIN}</Code>
      <H2>Create lead</H2>
      <P>Map our applicant to the lender’s fields; pass the lender’s id on as <C>lender_ref</C>.</P>
      <Code title="src/api/v1/create-lead.js">{LEAD}</Code>
      <H2>Get offer (and still deciding)</H2>
      <P>Read the lead id from the step before; map the offers to ours; <C>pending</C> when the lender is not done.</P>
      <Code title="src/api/v1/get-offer.js">{OFFER}</Code>
      <H2>Continue on the lender’s page</H2>
      <Code title="a redirect">{REDIRECT}</Code>
      <H2>Lender codes to outcomes</H2>
      <Code title="a table of codes">{CODES}</Code>
      <H2>A form body</H2>
      <Code title="application/x-www-form-urlencoded">{FORM}</Code>
      <H2>A signed request</H2>
      <Code title="HMAC signature">{SIGN}</Code>
      <H2>No reply and timeouts</H2>
      <P>When the lender does not answer in time, <C>reply.status</C> is <C>0</C> and <C>reply.body</C> is <C>null</C>. Always end with a line that returns <C>error</C>:</P>
      <Code>{`if (reply.status === 0) return { outcome: 'error', reply_code: 'NO_REPLY' }`}</Code>
      <H3>Checklist for every endpoint</H3>
      <List>
        <li>Every path returns <C>{'{ outcome, reply_code }'}</C>: the happy one, the lender’s no, its errors, no reply.</li>
        <li>Every returned outcome is in <C>outcomes</C>.</li>
        <li>A test event for each outcome.</li>
        <li><C>timeoutMs</C> on every call.</li>
        <li>No key in the code, no personal data in logs.</li>
      </List>
    </>
  )
}

export function EnvVariables() {
  return (
    <>
      <Lead>Variables are the lender’s settings and keys. They live in tab 5 · Env variables (the vault), never in the code. The code reads them with <C>ctx.env.NAME</C> or <C>ctx.secret('NAME')</C>.</Lead>
      <H2>Where they come from</H2>
      <Table head={['Variable', 'Set by', 'Value']} rows={[
        ['LENDER_BASE_URL', 'the Hub, from tab 1', 'the UAT or production base address'],
        ['LENDER_TOKEN', 'you (tab 5)', 'a fixed token, when the lender gives one that does not expire'],
        ['anything else', 'you (tab 5)', 'a UAT and a production value each'],
        ['PORT, HUB_ENVIRONMENT, NODE_ENV', 'the runner', 'never set them in tab 5'],
      ]} />
      <H2>How a new variable appears</H2>
      <List ordered>
        <li>Your code reads <C>ctx.secret('LENDER_CLIENT_SECRET')</C>.</li>
        <li><b>Discover endpoints</b> (or Build) finds the name and adds it to tab 5 with no value: “Found in the code, waiting for a value”.</li>
        <li>An admin or editor sets the UAT and production values once; afterwards they show masked.</li>
      </List>
      <H2>Which value the code gets</H2>
      <Table head={['Where the code runs', 'Values']} rows={[
        ['playground, UAT selected', 'UAT values'],
        ['playground, Production selected', 'production values (red bar)'],
        ['built container, UAT', 'UAT values'],
        ['live container (production)', 'production values'],
      ]} />
      <P>The values are written as <C>.env</C> into the sandbox or the container before it starts and again when tab 5 changes. They are never in git, in a bundle or in a log; every read is in the audit log (names only).</P>
      <H2>Naming</H2>
      <P>Capitals, digits and <C>_</C>, starting with a letter: <C>LENDER_API_KEY</C>, <C>LENDER_PARTNER_CODE</C>. Names with KEY, SECRET, TOKEN, PASS or AUTH in them are kept as secrets (masked).</P>
    </>
  )
}

const ERRORS: [string, string, string][] = [
  ['url must start with https://', 'the address is http:// or a path only', 'use ctx.env.LENDER_BASE_URL + path; check tab 1’s addresses'],
  ['url contains “undefined”', 'a value in the address is missing', 'an earlier step’s lender_ref (ctx.prev), or a variable not set in tab 5'],
  ['ctx.secret(\'X\'): no variable X for this lender', 'tab 5 has no value for X here', 'set X for UAT (and production) in tab 5'],
  ['missing variables: X (Env variables tab)', 'ctx.env.X was read but not set', 'set it, or give a default: ctx.env.X || \'…\''],
  ['reply_code is missing', 'a return without reply_code', 'add reply_code: the lender’s code or HTTP_<status>'],
  ['outcome “x” is not one the system knows', 'a typo, e.g. approve', 'use one of the 8 outcomes'],
  ['outcome “x” is not in this endpoint’s outcomes', 'returned but not listed', 'add it to outcomes: [...], then Discover endpoints'],
  ['outcome offer needs offers', 'offer with an empty list', 'return at least one offer, or another outcome'],
  ['offers[0].ratePa must be the yearly rate in %', 'e.g. 0.185', 'send 18.5'],
  ['offers[0].tenureMonths must be whole months above 0', 'e.g. "24" text or 2.5', 'Number(…), whole months'],
  ['link must be an https:// address', 'redirect with http or no link', 'check the lender’s field; https only'],
  ['timeoutMs must be a number from 1 to 30000', 'too long or text', 'at most 30 seconds'],
  ['no reply within 8000 ms (status 0)', 'the lender did not answer', 'whitelisting? the address? a longer timeoutMs'],
  ['defines no endpoint', 'a file in src/api without defineApi', 'export defineApi, or move the helper to src/services'],
  ['does not load: …', 'a syntax error or a wrong require path', 'the message names the line; ../../services/x from src/api/v1'],
  ['route POST /x is defined twice', 'two files make the same route', 'rename one'],
  ['field “x” is not part of the standard outcome', 'an extra field in the answer (warning)', 'put it under data: { … }'],
  ['Build: Save first / after Run all tests is green', 'unsaved changes or red tests', 'Save, Run all tests until all are green'],
  ['the call does not match the system', 'a rule of the call broken', 'the text after it says which: Outcome → system check'],
]

export function Errors() {
  return (
    <>
      <Lead>Every message the SDK and the playground show, what it means and what to do. Look in the Console first: it shows each call, the lender’s reply and your answer.</Lead>
      <H2>Messages</H2>
      <Table head={['Message', 'Means', 'Do']} rows={ERRORS.map((e) => [...e])} code={false} />
      <H2>How to find a problem</H2>
      <List ordered>
        <li>Open the Console: did the call go out? which URL (keys masked)? what status?</li>
        <li>Status <C>0</C>: the lender did not answer → the whitelist, the address, the timeout.</li>
        <li>Status 4xx: our request is wrong → compare the body with the lender’s document (try it in HTTP request first).</li>
        <li>The reply is fine but the test is red → your mapping: print codes with <C>ctx.log</C>, compare with the expected outcome.</li>
        <li>Works in the playground, not after Build → a package not in <C>dependencies</C>, or a variable set for UAT only.</li>
      </List>
    </>
  )
}

export function Rules() {
  return (
    <>
      <Lead>The rules every batch follows. Most are checked by the Hub; the others are our law and security rules.</Lead>
      <Table head={['Rule', 'Why']} code={false} rows={[
        ['Call the lender only with ctx.lender()', 'tests, captured codes and the checks only work through it'],
        ['No key, password or token in the code or in git', 'keys live in tab 5 (the vault); the code reads ctx.secret(\'NAME\')'],
        ['https:// only, from ctx.env.LENDER_BASE_URL', 'the same code runs on UAT and production'],
        ['timeoutMs on every call, at most 30 s', 'the web app waits for the whole workflow'],
        ['Every path returns { outcome, reply_code }', 'a missing return is a red test and an error answer'],
        ['No name, mobile, PAN or date of birth in ctx.log, reply_code, message or data', 'logs keep codes and ids only (the law and our rule)'],
        ['Send ctx.ref as the partner reference', 'ties the lender’s record to ours without personal data'],
        ['One endpoint per file in src/api; helpers in src/services', 'Discover reads every file in src/api as an endpoint'],
        ['Few packages', 'each one is code we run; Node 22 has fetch, crypto, URLSearchParams'],
        ['Test on UAT; Production only for one final check', 'production calls reach the real lender and are recorded'],
      ]} />
      <H2>What the Hub does for safety</H2>
      <List>
        <li>Each lender’s code runs in its own locked container: no privileges, memory / CPU / process limits, no access to our databases or other services.</li>
        <li>Variables are written into the container, never shown again in full; every read is audited.</li>
        <li>Lender replies in the Console and run log are masked (keys and tokens cut to 7 characters).</li>
        <li>Requests from the HTTP client cannot reach our private addresses.</li>
      </List>
    </>
  )
}

export function Glossary() {
  return (
    <>
      <Lead>The words used in the Hub, in one place.</Lead>
      <H2>Words</H2>
      <Table head={['Word', 'Means']} code={false} rows={[
        [<b>Batch</b>, 'all of one lender’s endpoint code: a Node.js service with a version (lender.batchApi 1.2.0)'],
        [<b>Endpoint (your code)</b>, 'one file in src/api: one lender call and its outcome'],
        [<b>Service endpoint</b>, 'the address the web app calls for one product (e.g. moneyview.personal), behind one workflow version'],
        [<b>Workflow</b>, 'the order of endpoints on the canvas, from Start to an Answer'],
        [<b>Outcome</b>, 'the result of one step: ok, offer, approved, redirect, pending, rejected, duplicate, error'],
        [<b>Answer</b>, 'what the web app gets at the end of a workflow'],
        [<b>reply_code</b>, 'the lender’s own code for a reply, kept in the run log'],
        [<b>Captured code</b>, 'a lender code the Hub saw; a new one raises an Alert'],
        [<b>Test event</b>, 'a saved lender reply and the outcome the code must give for it'],
        [<b>Sandbox</b>, 'your container in the playground, one per lender and version'],
        [<b>Build</b>, 'makes the image and fixes the version, after all tests pass'],
        [<b>Make live</b>, 'production runs this version; the one before stays as previous'],
        [<b>Roll back</b>, 'back to the previous version in one step'],
        [<b>UAT</b>, 'the lender’s test system'],
        [<b>Env variables</b>, 'the lender’s settings and keys in the vault (tab 5)'],
        [<b>Manifest</b>, 'what Discover found: endpoints, outcomes, variables (.hub/manifest.json)'],
        [<b>SDK</b>, '@firstfund/lender-sdk, the package your code uses'],
        [<b>Run log</b>, 'every workflow run for 30 days: steps, answer, reference ids, no personal data'],
      ]} />
      <H2>Where the code lives</H2>
      <Table head={['Part', 'Folder']} rows={[
        ['the SDK', 'firstfund-lender-hub/sdk'],
        ['the Hub API (runner, workflows, sandboxes)', 'firstfund-lender-hub/api2'],
        ['this portal and these docs', 'firstfund-lender-hub/portal (docs: src/screens/docs)'],
        ['the contracts (JSON Schemas)', 'firstfund-contracts/schemas/hub'],
      ]} />
      <P>Start again at the <DocLink to="introduction">Introduction</DocLink>.</P>
    </>
  )
}
